import { useEffect, useState } from 'react'

import { pushLocationAnalytics } from './analytics'
import { LocationItem } from './SearchJobRedirectProps'
import { getAllChildValues } from './utils'

interface LocationDropdownProps {
  locations: LocationItem[]
  selectedLocations: string[]
  onApply: (locations: string[]) => void
  placeholder: string
  dataDm?: string
}

export function LocationDropdown({ locations, selectedLocations, onApply, placeholder, dataDm }: LocationDropdownProps) {
  const [tempSelectedLocations, setTempSelectedLocations] = useState<string[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
  const [locationsWithSelection, setLocationsWithSelection] = useState<LocationItem[]>([])

  useEffect(() => {
    if (!isOpen) {
      setTempSelectedLocations([...selectedLocations])
    }
  }, [isOpen, selectedLocations])

  useEffect(() => {
    const updateSelectionStates = (items: LocationItem[]): LocationItem[] => {
      return items.map(item => {
        const updatedItem = { ...item }
        
        if (item.children) {
          updatedItem.children = updateSelectionStates(item.children)
          updatedItem.selected = updatedItem.children.some(child => child.selected)
        } else {
          updatedItem.selected = tempSelectedLocations.includes(item.value)
        }
        
        return updatedItem
      })
    }
    
    setLocationsWithSelection(updateSelectionStates(locations))
  }, [tempSelectedLocations, locations])

  const toggleDropdown = () => {
    if (!isOpen) {
      setTempSelectedLocations([...selectedLocations])
    }
    setIsOpen(!isOpen)
  }

  const toggleExpand = (itemKey: string, e: React.MouseEvent) => {
    e.stopPropagation()
    const newExpanded = new Set(expandedItems)
    if (newExpanded.has(itemKey)) {
      newExpanded.delete(itemKey)
    } else {
      newExpanded.add(itemKey)
    }
    setExpandedItems(newExpanded)
  }

  const handleCheckboxClick = (item: LocationItem, e: React.MouseEvent) => {
    e.stopPropagation()
    
    const hasChildren = item.children && item.children.length > 0
    
    if (hasChildren) {
      const allChildValues = getAllChildValues(item)
      const allSelected = allChildValues.every(childValue => tempSelectedLocations.includes(childValue))
      
      if (allSelected) {
        setTempSelectedLocations(prev => prev.filter(loc => !allChildValues.includes(loc)))
      } else {
        setTempSelectedLocations(prev => [...new Set([...prev, ...allChildValues])])
        setExpandedItems(prev => new Set(prev).add(item.key))
      }
    } else {
      const isSelected = tempSelectedLocations.includes(item.value)
      
      if (isSelected) {
        setTempSelectedLocations(prev => prev.filter(loc => loc !== item.value))
      } else {
        setTempSelectedLocations(prev => [...prev, item.value])
      }
    }
  }

  const applyFilter = () => {
    onApply([...tempSelectedLocations])
    pushLocationAnalytics(tempSelectedLocations)
    setIsOpen(false)
  }

  const resetFilter = () => {
    setTempSelectedLocations([])
    onApply([])
  }

  const handleCheckboxKeyUp = (e: React.KeyboardEvent, item: LocationItem) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleCheckboxClick(item, e as unknown as React.MouseEvent)
    }
  }

  const handleCheckboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault()
    }
  }

  const renderLocationItems = (items: LocationItem[], level: number = 0): JSX.Element[] => {
    return items.map((item, index) => {
      const hasChildren = item.children && item.children.length > 0
      const isExpanded = expandedItems.has(item.key)
      const isSelected = item.selected || false

      return (
        <li
          key={`${item.key}-${index}`}
          className={`cmp-filterbar__checkbox-item ${!hasChildren ? 'cmp-filterbar__checkbox-item--is-nested' : ''}`}
        >
          <div className="cmp-filterbar__checkbox-item-wrapper">
            <div
              className={`cmp-filterbar__checkbox-input ${isSelected ? 'cmp-filterbar__checkbox-input--is-checked' : ''}`}
              role="checkbox"
              aria-checked={isSelected}
              aria-label={item.value}
              onClick={(e) => handleCheckboxClick(item, e)}
              onKeyUp={(e) => handleCheckboxKeyUp(e, item)}
              onKeyDown={handleCheckboxKeyDown}
              tabIndex={0}
            >
              <span>{item.value}</span>
            </div>
            
            {hasChildren && (
              <button
                onClick={(e) => toggleExpand(item.key, e)}
                className={`cmp-filterbar__checkbox-expand icon--md ${isExpanded ? 'icon-keyboard_arrow_up' : 'icon-keyboard_arrow_down'}`}
                aria-label={isExpanded ? 'Collapse' : 'Expand'}
                type="button"
                tabIndex={0}
              />
            )}
          </div>
          
          {hasChildren && isExpanded && (
            <ul className="cmp-filterbar__child-checkboxes cmp-filterbar__child-checkboxes--is-expanded">
              {renderLocationItems(item.children!, level + 1)}
            </ul>
          )}
        </li>
      )
    })
  }

  const getSelectedLocationLabel = () => {
    if (selectedLocations.length === 0) return placeholder
    if (selectedLocations.length === 1) return selectedLocations[0]
    return `${selectedLocations.length} locations selected`
  }

  return (
    <div className="cmp-filterbar__filter-dropdown">
      <div className={`cmp-filterbar__dropdown ${isOpen ? 'cmp-filterbar__dropdown--is-open' : ''}`}>
        <button
          className="cmp-filterbar__dropdown-button"
          onClick={toggleDropdown}
          aria-label="Filter toggle for: Location"
          aria-expanded={isOpen}
          type="button"
          data-dm={dataDm || null}
        >
          <span className="location-group-label">{getSelectedLocationLabel()}</span>
        </button>

        <div 
          role="group" 
          aria-labelledby="location-group-label" 
          className="cmp-filterbar__dropdown-container"
        >
          <ul className="cmp-filterbar__dropdown-checkboxes">
            {locationsWithSelection.length > 0 ? (
              renderLocationItems(locationsWithSelection)
            ) : (
              <li className="cmp-filterbar__no-results">No locations available</li>
            )}
          </ul>
        </div>

        <div className="cmp-filterbar__dropdown-footer">
          <button
            className="cmp-filterbar__button cmp-filterbar__button--reset"
            onClick={resetFilter}
            aria-label="RESET: Location"
            type="button"
          >
            <span className="icon--sm icon-reset icon-placement--before-label"></span>
            <span className="cmp-filterbar__button-text">Reset</span>
          </button>
          <button
            className="cmp-filterbar__button cmp-filterbar__button--select"
            onClick={applyFilter}
            aria-label="SELECT: Location"
            type="button"
          >
            <span className="cmp-filterbar__button-text">Select</span>
          </button>
        </div>
      </div>
    </div>
  )
}

